import React, { Component } from 'react';
import { Menu } from '../../components/Menu/Menu';
import LoadingWindow from '../../components/LoadingWindow';
import { confirmCloseAlert } from '../../functions/alerts';
import { listProductLines, getNextProductLine, saveProductLine, deactivateProductLine } from '../../services/maintenanceService';
import Swal from 'sweetalert2';
import { TbStack2, TbTag } from 'react-icons/tb';
import { AiOutlineSearch, AiOutlineSave } from 'react-icons/ai';
import { BsPlusCircle, BsPencilSquare, BsSlashCircle, BsXCircle } from 'react-icons/bs';
import { IoCloseCircle } from 'react-icons/io5';
import styles from '../../css/ProductLines.module.css';

// Devuelve el primer valor no nulo/indefinido (reemplazo de `??`).
function val() {
    for (let i = 0; i < arguments.length; i++) {
        if (arguments[i] !== undefined && arguments[i] !== null) return arguments[i];
    }
    return undefined;
}
const isBlank = (x) => String(x === undefined || x === null ? '' : x).trim() === '';

// ─── Modelo ───────────────────────────────────────────────────────────────────
export class ProductLine {
    constructor(p = {}) {
        this.productLine = val(p.productLine, p.ProductLine, '');
        this.productLineDesc = val(p.productLineDesc, p.ProductLineDesc, '');
        this.markup = val(p.markup, p.DiscountMarkup, p.Markup, '');
        this.active = val(p.active, p.Active, p.Visible, 1) ? 1 : 0;
    }
}

export function emptyProductLineForm() {
    return { productLine: '', productLineDesc: '', markup: '' };
}

// Validación: ningún campo vacío; markup numérico (porcentaje).
export function validateProductLine(f) {
    const e = {};
    if (isBlank(f.productLineDesc)) e.productLineDesc = 'Required';
    const m = String(val(f.markup, '')).trim();
    if (m === '') e.markup = 'Required';
    else if (isNaN(Number(m)) || Number(m) < 0) e.markup = 'Enter a valid percentage';
    return e;
}

export default class ProductLines extends Component {
    constructor(props) {
        super(props);
        this.ModalLoading = React.createRef();
        this.state = {
            all: [],
            search: '',
            selected: null,
            showModal: false,
            isEdit: false,
            form: emptyProductLineForm(),
            errors: {},
            saving: false,
        };
    }

    componentDidMount() { this.load(); }

    async load() {
        this.ModalLoading.current.showState(true);
        const data = await listProductLines();
        this.ModalLoading.current.showState(false);
        const all = (data || []).map(p => new ProductLine(p));
        this.setState({ all });
    }

    openCreate = async () => {
        this.ModalLoading.current.showState(true);
        const nextNo = await getNextProductLine();  // último registro + 1 (backend)
        this.ModalLoading.current.showState(false);
        this.setState({
            showModal: true, isEdit: false, errors: {},
            form: { ...emptyProductLineForm(), productLine: val(nextNo, '') },
        });
    };

    openEdit = (p) => this.setState({ showModal: true, isEdit: true, errors: {}, form: { ...p } });

    closeModal = () => this.setState({ showModal: false });

    onField = (key, value) => {
        this.setState(prev => ({ form: { ...prev.form, [key]: value }, errors: { ...prev.errors, [key]: undefined } }));
    };

    submit = async () => {
        const { form, isEdit } = this.state;
        const errors = validateProductLine(form);
        if (Object.keys(errors).length > 0) { this.setState({ errors }); return; }

        this.setState({ saving: true });
        const okSave = await saveProductLine({ ...form, markup: Number(form.markup) }, isEdit);
        this.setState({ saving: false });
        if (okSave) {
            confirmCloseAlert('correct', `Product line ${isEdit ? 'updated' : 'created'} successfully.`);
            this.setState({ showModal: false });
            this.load();
            if (isEdit) this.setState({ selected: new ProductLine(form) });
        } else {
            confirmCloseAlert('incorrect', `Could not ${isEdit ? 'update' : 'create'} the product line. Please try again.`);
        }
    };

    confirmDeactivate = (p) => {
        Swal.fire({
            title: 'Deactivate product line?',
            html: `<b>${p.productLineDesc}</b> (${p.productLine}) will be marked inactive.`,
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#EF4444', cancelButtonColor: '#64748B',
            confirmButtonText: 'Yes, deactivate', cancelButtonText: 'Cancel',
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            this.ModalLoading.current.showState(true);
            const okDe = await deactivateProductLine(p.productLine);
            this.ModalLoading.current.showState(false);
            if (okDe) {
                confirmCloseAlert('correct', 'Product line deactivated.');
                this.setState({ selected: null });
                this.load();
            } else {
                confirmCloseAlert('incorrect', 'Could not deactivate the product line.');
            }
        });
    };

    render() {
        const { all, search, selected, showModal, isEdit, form, errors, saving } = this.state;
        const q = search.toLowerCase();
        const filtered = all.filter(p => !q || (`${p.productLineDesc} ${p.productLine}`).toLowerCase().includes(q));

        return (
            <div className={styles.page}>
                <Menu />
                <LoadingWindow ref={this.ModalLoading} />

                {/* Hero */}
                <div className={styles.hero}>
                    <div className={styles.heroLeft}>
                        <div className={styles.heroIconBox}>
                            <TbStack2 className={styles.heroIcon} />
                        </div>
                        <div>
                            <h1 className={styles.heroTitle}>Product Lines</h1>
                            <p className={styles.heroSubtitle}>{all.length} product lines · Create and maintain product line records</p>
                        </div>
                    </div>
                    <button className={styles.heroBtn} onClick={this.openCreate}>
                        <BsPlusCircle /> New Product Line
                    </button>
                </div>

                <div className={styles.body}>
                    {/* LEFT — lista */}
                    <div className={styles.listPanel}>
                        <div className={styles.searchWrap}>
                            <div className={styles.searchBox}>
                                <AiOutlineSearch className={styles.searchIcon} />
                                <input
                                    className={styles.searchInput}
                                    type="text"
                                    placeholder="Search product lines..."
                                    value={search}
                                    onChange={e => this.setState({ search: e.target.value })}
                                />
                                {search && (
                                    <IoCloseCircle className={styles.searchClear} title="Clear search" onClick={() => this.setState({ search: '' })} />
                                )}
                            </div>
                        </div>
                        <div className={styles.list}>
                            {filtered.length === 0 && <div className={styles.emptyRow}>No product lines found</div>}
                            {filtered.map((p, i) => {
                                const isSel = selected && selected.productLine === p.productLine;
                                return (
                                    <div key={i} onClick={() => this.setState({ selected: p })}
                                        className={`${styles.item} ${isSel ? styles.itemActive : ''}`}>
                                        <div className={styles.itemAvatar}><TbTag /></div>
                                        <div className={styles.itemInfo}>
                                            <div className={styles.itemName}>{p.productLineDesc}</div>
                                            <div className={styles.itemSub}>{p.productLine}{p.markup !== '' ? ` · ${p.markup}%` : ''}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* RIGHT — detalle */}
                    <div className={styles.detail}>
                        {!selected ? (
                            <div className={styles.emptyState}>
                                <TbStack2 className={styles.emptyIcon} />
                                <p className={styles.emptyText}>Select a product line from the list</p>
                            </div>
                        ) : (
                            <>
                                <div className={styles.detailHeader}>
                                    <div className={styles.detailAvatar}><TbTag /></div>
                                    <div className={styles.detailHeaderInfo}>
                                        <div className={styles.detailTitle}>{selected.productLineDesc}</div>
                                        <span className={styles.badge}>{selected.productLine}</span>
                                    </div>
                                    <div className={styles.detailActions}>
                                        <button className={styles.btnPrimary} onClick={() => this.openEdit(selected)}><BsPencilSquare /> Edit</button>
                                        <button className={styles.btnDanger} onClick={() => this.confirmDeactivate(selected)}><BsSlashCircle /> Deactivate</button>
                                    </div>
                                </div>

                                <div className={styles.card}>
                                    <div className={styles.cardHeader}>
                                        <span className={styles.cardHeaderTitle}>Product Line Information</span>
                                    </div>
                                    <div className={styles.cardBody}>
                                        <div className="row g-3">
                                            {[
                                                ['Product Line', selected.productLine],
                                                ['Description', selected.productLineDesc],
                                                ['Markup %', selected.markup !== '' ? `${selected.markup}%` : ''],
                                            ].map(([k, value], i) => (
                                                <div key={i} className="col-12 col-md-6">
                                                    <label className={styles.fieldLabel}>{k}</label>
                                                    <div className={styles.fieldValue}>{value || <span className={styles.fieldValueEmpty}>—</span>}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Modal crear/editar */}
                {showModal && (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modal}>
                            <div className={styles.modalHeader}>
                                <div>
                                    <div className={styles.modalTitle}>{isEdit ? 'Edit Product Line' : 'Create New Product Line'}</div>
                                    <div className={styles.modalSubtitle}>Product Line No is assigned by the system and cannot be edited</div>
                                </div>
                                <button className={styles.modalClose} onClick={this.closeModal}><BsXCircle /></button>
                            </div>
                            <div className={styles.modalBody}>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className={styles.formLabel}>Product Line No (system)</label>
                                        <input className={`${styles.input} ${styles.inputDisabled}`} value={form.productLine || ''} disabled placeholder="Assigned by the system" />
                                    </div>
                                    <div className="col-12">
                                        <label className={styles.formLabel}>Description<span className={styles.required}> *</span></label>
                                        <input className={`${styles.input} ${errors.productLineDesc ? styles.inputError : ''}`} value={form.productLineDesc || ''} onChange={e => this.onField('productLineDesc', e.target.value)} />
                                        {errors.productLineDesc && <div className={styles.formError}>{errors.productLineDesc}</div>}
                                    </div>
                                    <div className="col-6">
                                        <label className={styles.formLabel}>Markup %<span className={styles.required}> *</span></label>
                                        <div className={styles.pctWrap}>
                                            <input
                                                type="number" min="0" step="0.01"
                                                className={`${styles.input} ${styles.pctInput} ${errors.markup ? styles.inputError : ''}`}
                                                value={form.markup}
                                                onChange={e => this.onField('markup', e.target.value)}
                                            />
                                            <span className={styles.pctSign}>%</span>
                                        </div>
                                        {errors.markup && <div className={styles.formError}>{errors.markup}</div>}
                                    </div>
                                </div>
                                <div className={styles.modalActions}>
                                    <button className={styles.btnCancel} onClick={this.closeModal}><BsXCircle /> Cancel</button>
                                    <button className={styles.btnSubmit} onClick={this.submit} disabled={saving}>
                                        {isEdit ? <><AiOutlineSave /> Save Changes</> : <><BsPlusCircle /> Create Product Line</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
